import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { ArrowRight, Mail, Lock, Eye } from 'lucide-react';
export function LoginPage() {
  const navigate = useNavigate();
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    // Always run onboarding on every login
    localStorage.removeItem('jua-user-profile');
    navigate('/onboarding');
  };
  return <div className="min-h-screen flex w-full bg-white font-sans" data-id="element-3820">
      {/* Left Side - Dark Branding */}
      <div className="hidden lg:flex flex-col w-[55%] bg-[#0B1120] relative overflow-hidden border-r border-slate-800" data-id="element-3821">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1120] via-[#110a14] to-[#1a0a12]" data-id="element-3822" />

        <div className="relative z-10 flex flex-col h-full p-12 overflow-y-auto" data-id="element-3823">
          {/* All content as one vertically centered block */}
          <div className="flex-1 flex flex-col justify-center" data-id="element-3824">
            {/* Logo + Tag */}
            <Link to="/" className="flex items-center gap-2.5 mb-3" data-id="element-3825">
              <img src="https://cdn.magicpatterns.com/uploads/iXnnzvi4wVFko9E21giff4/pasted-image.png" alt="Jua logo" className="w-8 h-8 object-contain flex-shrink-0" data-id="element-3826" />
              <span className="text-2xl font-light tracking-tight text-white" data-id="element-3827">
                Jua<span className="text-[#EC4899]" data-id="element-3828">.ai</span>
              </span>
            </Link>

            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 w-fit mb-6" data-id="element-3829">
              <div className="w-2 h-2 rounded-full bg-pink-500" data-id="element-3830" />
              <span className="text-xs font-medium text-slate-300" data-id="element-3831">
                Powered by EPT-2
              </span>
            </div>

            <h1 className="text-5xl font-bold text-white leading-[1.15] mb-4 tracking-tight" data-id="element-3832">
              Your AI Analyst for <br data-id="element-3833" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#f0407a] to-[#a855f7]" data-id="element-3834">
                Energy Trading
              </span>
            </h1>

            <p className="text-base text-slate-400 mb-10 leading-relaxed max-w-[420px]" data-id="element-3835">
              Ask anything about weather patterns, energy markets, or analyze
              specific data points with our proprietary model.
            </p>

            {/* App Screenshot */}
            <div className="w-full rounded-xl overflow-hidden shadow-2xl shadow-pink-500/10 border border-slate-800 relative" data-id="element-3836">
              <img src="https://cdn.magicpatterns.com/uploads/3iHpycm9FaSj7kxSJ83h2m/pasted-image.png" alt="Jua.ai platform screenshot" className="w-full h-auto block" data-id="element-3837" />
              <div className="absolute inset-0 bg-gradient-to-tr from-pink-500/5 via-transparent to-purple-500/5 pointer-events-none" data-id="element-3838" />
            </div>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 pt-8" data-id="element-3839">
            <p className="text-xs font-bold text-slate-500 tracking-widest uppercase mb-4" data-id="element-3840">
              Trusted by leading trading desks
            </p>
            <div className="flex gap-6 opacity-40" data-id="element-3841">
              <div className="h-6 w-20 bg-slate-600 rounded" data-id="element-3842" />
              <div className="h-6 w-24 bg-slate-600 rounded" data-id="element-3843" />
              <div className="h-6 w-16 bg-slate-600 rounded" data-id="element-3844" />
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-[45%] flex flex-col justify-center items-center px-8 py-6 relative min-h-screen overflow-y-auto" data-id="element-3845">
        <div className="w-full max-w-md" data-id="element-3846">
          <div className="mb-10 text-center lg:text-left" data-id="element-3847">
            <h2 className="text-3xl font-bold text-slate-900 mb-2" data-id="element-3848">
              Welcome back
            </h2>
            <p className="text-slate-500" data-id="element-3849">
              Please enter your details to sign in.
            </p>
          </div>

          {/* OAuth Buttons */}
          <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-3850">
            <button className="flex items-center justify-center gap-2.5 w-full h-12 rounded-[12px] border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-3851">
              <svg className="w-5 h-5" viewBox="0 0 24 24" data-id="element-3852">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" data-id="element-3853" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-3854" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" data-id="element-3855" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-3856" />
              </svg>
              Google
            </button>
            <button className="flex items-center justify-center gap-2.5 w-full h-12 rounded-[12px] border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-3857">
              <svg className="w-5 h-5" viewBox="0 0 21 21" data-id="element-3858">
                <rect x="1" y="1" width="9" height="9" fill="#f25022" data-id="element-3859" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" data-id="element-3860" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" data-id="element-3861" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" data-id="element-3862" />
              </svg>
              Microsoft
            </button>
          </div>

          <div className="relative flex items-center py-4 mb-4" data-id="element-3863">
            <div className="flex-grow border-t border-slate-200" data-id="element-3864"></div>
            <span className="flex-shrink-0 mx-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-3865">
              Or continue with email
            </span>
            <div className="flex-grow border-t border-slate-200" data-id="element-3866"></div>
          </div>

          {/* Form */}
          <form onSubmit={handleLogin} className="space-y-5" data-id="element-3867">
            <div className="space-y-1.5" data-id="element-3868">
              <label className="text-sm font-medium text-slate-700" data-id="element-3869">
                Email address
              </label>
              <div className="relative" data-id="element-3870">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} data-id="element-3871" />
                <Input type="email" placeholder="demo@jua.ai" className="pl-10 h-11 bg-white border-slate-200 text-slate-900 w-full" data-id="element-3872" />
              </div>
            </div>

            <div className="space-y-1.5" data-id="element-3873">
              <label className="text-sm font-medium text-slate-700" data-id="element-3874">
                Password
              </label>
              <div className="relative" data-id="element-3875">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} data-id="element-3876" />
                <Input type="password" placeholder="••••••••" className="pl-10 pr-10 h-11 bg-white border-slate-200 text-slate-900 w-full" data-id="element-3877" />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-3878">
                  <Eye size={18} data-id="element-3879" />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2" data-id="element-3880">
              <label className="flex items-center gap-2.5 cursor-pointer group" data-id="element-3881">
                <div className="relative flex items-center justify-center" data-id="element-3882">
                  <input type="checkbox" className="peer sr-only" data-id="element-3883" />
                  <div className="w-[18px] h-[18px] rounded-[5px] border border-slate-300 bg-white peer-checked:bg-pink-500 peer-checked:border-pink-500 peer-focus-visible:ring-2 peer-focus-visible:ring-pink-500/30 transition-all group-hover:border-slate-400" data-id="element-3884" />
                  <svg className="absolute w-3 h-3 text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none" viewBox="0 0 12 12" fill="none" data-id="element-3885">
                    <path d="M2.5 6L5 8.5L9.5 3.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" data-id="element-3886" />
                  </svg>
                </div>
                <span className="text-sm text-slate-600" data-id="element-3887">Remember me</span>
              </label>
              <a href="#" className="text-sm font-semibold text-pink-600 hover:text-pink-700" data-id="element-3888">
                Forgot password?
              </a>
            </div>

            <Button type="submit" size="lg" className="w-full mt-6 gap-2 text-base h-12" data-id="element-3889">
              Sign in <ArrowRight size={18} data-id="element-3890" />
            </Button>
          </form>

          <p className="text-center mt-8 text-sm text-slate-600" data-id="element-3891">
            Don't have an account?{' '}
            <a href="#" className="font-semibold text-slate-900 hover:underline" data-id="element-3892">
              Request access
            </a>
          </p>
        </div>

        <div className="absolute bottom-8 right-8 text-xs text-slate-400" data-id="element-3893">
          © 2026 Jua.ai
        </div>
      </div>
    </div>;
}